/**
 * EmailPreviewSheet — Epic 5, Story 5.3 (ADR-090)
 *
 * Sablon választó + címzett lista + megerősítés drawer.
 * A kijelölt partnereknek bulk email küldés indítása.
 */

import type { BulkSendResult } from '@/api/email';
import { bulkSendEmail } from '@/api/email';
import { useTranslation } from '@kgc/i18n';
import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@kgc/ui';
import { useCallback, useState } from 'react';

// ============================================
// TEMPLATE OPTIONS
// ============================================

const TEMPLATE_OPTIONS = [
  { slug: 'invoice-sent', labelKey: 'email.template.invoiceSent' },
  { slug: 'invoice-overdue', labelKey: 'email.template.invoiceOverdue' },
  { slug: 'rental-confirmation', labelKey: 'email.template.rentalConfirmation' },
  { slug: 'rental-overdue', labelKey: 'email.template.rentalOverdue' },
  { slug: 'rental-deposit-warning', labelKey: 'email.template.rentalDepositWarning' },
  { slug: 'worksheet-created', labelKey: 'email.template.worksheetCreated' },
  { slug: 'worksheet-ready', labelKey: 'email.template.worksheetReady' },
  { slug: 'quote-sent', labelKey: 'email.template.quoteSent' },
  { slug: 'welcome-partner', labelKey: 'email.template.welcomePartner' },
  { slug: 'payment-received', labelKey: 'email.template.paymentReceived' },
] as const;

const LANGUAGE_OPTIONS = [
  { value: 'hu', label: 'Magyar' },
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
  { value: 'uk', label: 'Українська' },
] as const;

// ============================================
// PROPS
// ============================================

export interface EmailRecipient {
  id: string;
  name: string;
  email?: string | null | undefined;
}

export interface EmailPreviewSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipients: EmailRecipient[];
  onSendComplete?: (() => void) | undefined;
}

// ============================================
// COMPONENT
// ============================================

export function EmailPreviewSheet({
  open,
  onOpenChange,
  recipients,
  onSendComplete,
}: EmailPreviewSheetProps) {
  const { t } = useTranslation();
  const [selectedTemplate, setSelectedTemplate] = useState<string>(TEMPLATE_OPTIONS[0].slug);
  const [selectedLanguage, setSelectedLanguage] = useState('hu');
  const [isSending, setIsSending] = useState(false);
  const [showAllRecipients, setShowAllRecipients] = useState(false);

  const recipientsWithEmail = recipients.filter(r => r.email);
  const recipientsWithoutEmail = recipients.filter(r => !r.email);
  const displayedRecipients = showAllRecipients
    ? recipientsWithEmail
    : recipientsWithEmail.slice(0, 5);

  const handleSend = useCallback(async () => {
    setIsSending(true);
    try {
      // M-2 fix: only send recipients that have email addresses
      const result: BulkSendResult = await bulkSendEmail({
        partnerIds: recipientsWithEmail.map(r => r.id),
        templateSlug: selectedTemplate,
        language: selectedLanguage,
      });

      if (result.failed === 0 && result.skipped === 0) {
        toast.success(t('email.bulkSend.successAll', { count: String(result.queued) }));
      } else if (result.queued > 0) {
        toast.warning(
          t('email.bulkSend.partialSuccess', {
            queued: String(result.queued),
            skipped: String(result.skipped),
          })
        );
      } else {
        toast.error(t('email.bulkSend.allFailed'));
      }

      onOpenChange(false);
      onSendComplete?.();
    } catch {
      toast.error(t('email.bulkSend.error'));
    } finally {
      setIsSending(false);
    }
  }, [recipients, selectedTemplate, selectedLanguage, onOpenChange, onSendComplete, t]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{t('email.bulkSend.title')}</SheetTitle>
          <SheetDescription>{t('email.bulkSend.description')}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Template selector */}
          <div>
            <label className="mb-1 block text-sm font-medium text-muted-foreground">
              {t('email.bulkSend.selectTemplate')}
            </label>
            <select
              value={selectedTemplate}
              onChange={e => setSelectedTemplate(e.target.value)}
              disabled={isSending}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground"
            >
              {TEMPLATE_OPTIONS.map(({ slug, labelKey }) => (
                <option key={slug} value={slug}>
                  {t(labelKey)}
                </option>
              ))}
            </select>
          </div>

          {/* Language selector */}
          <div>
            <label className="mb-1 block text-sm font-medium text-muted-foreground">
              {t('email.bulkSend.selectLanguage')}
            </label>
            <div className="flex gap-1">
              {LANGUAGE_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSelectedLanguage(value)}
                  className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
                    selectedLanguage === value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Recipients list */}
          <div>
            <label className="mb-2 block text-sm font-medium text-muted-foreground">
              {t('email.bulkSend.recipients')} ({recipientsWithEmail.length})
            </label>
            <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2 dark:border-slate-600">
              {displayedRecipients.map(r => (
                <div
                  key={r.id}
                  className="flex items-center justify-between rounded px-2 py-1 text-sm"
                >
                  <span className="font-medium text-foreground">{r.name}</span>
                  <span className="text-muted-foreground">{r.email}</span>
                </div>
              ))}
              {!showAllRecipients && recipientsWithEmail.length > 5 && (
                <button
                  type="button"
                  onClick={() => setShowAllRecipients(true)}
                  className="w-full py-1 text-center text-sm text-primary hover:underline"
                >
                  + {recipientsWithEmail.length - 5} {t('email.bulkSend.moreRecipients')}
                </button>
              )}
            </div>
          </div>

          {/* Skipped (no email) warning */}
          {recipientsWithoutEmail.length > 0 && (
            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-900/20">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                {t('email.bulkSend.noEmailWarning', {
                  count: String(recipientsWithoutEmail.length),
                })}
              </p>
            </div>
          )}

          {/* Info */}
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              {t('email.bulkSend.asyncInfo')}
            </p>
          </div>
        </div>

        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSend} disabled={isSending || recipientsWithEmail.length === 0}>
            {isSending
              ? t('email.bulkSend.sending')
              : t('email.bulkSend.sendButton', {
                  count: String(recipientsWithEmail.length),
                })}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
